import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { RoughFinish } from "@enums/roughFinish.enum";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { MaterialType } from "@enums/materialType.enum";
import { CuttingToolType } from "@enums/cuttingToolType.enum";
import { Department } from "@enums/department.enum";
import { Machine } from "@interfaces/models/machine.interface";
import { CuttingTool } from "@interfaces/models/cuttingTool.interface";
import { CuttingToolSpeedFeedSetting } from "@interfaces/models/cuttingToolSpeedFeedSetting.interface";
import { ProductionToolProgram } from "@interfaces/models/productionToolProgram.interface";
import { ProductionToolProgramLine } from "@interfaces/models/productionToolProgramLine.interface";
import { ToolHolder } from "@interfaces/models/toolHolder.interface";
import { ToolHolderMachine } from "@interfaces/models/toolHolderMachine.interface";
import { SupportingDocumentFileLineData } from "@interfaces/supportingDocumentFileLineData.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { CuttingToolService } from "@services/cuttingTool.service";
import { MachineService } from "@services/machine.service";
import { ProductionToolService } from "@services/productionTool.service";
import { ProductionToolPartService } from "@services/productionToolPart.service";
import { ProductionToolProgramService } from "@services/productionToolProgram.service";
import { ProductionToolProgramLineService } from "@services/productionToolProgramLine.service";
import { SystemParameterService } from "@services/systemParameter.service";
import { ToolHolderService } from "@services/toolHolder.service";
import { ToolHolderMachineService } from "@services/toolHolderMachine.service";
import { ToolOrderService } from "@services/toolOrder.service";
import { UserProfileService } from "@services/userProfile.service";
import { OperationService } from "@services/operation.service";
import { forkJoin, of } from "rxjs";
import { environment } from "@environments/environment";
import { FileService } from "@services/file.service";
import { ToastService } from "angular-toastify";
import { ToastApiError } from "@helpers/apiError";

@Component({
	moduleId: module.id,
	selector: "productionToolPrograms-id",
	styleUrls: ["productionToolPrograms-id.css"],
	templateUrl: "productionToolPrograms-id.html"
})
export class ProductionToolProgramsIdAdminComponent {
	blocks: any[] = [];
	createEnabled = true;
	cuttingToolTypes = GetPropertiesFromEnum(CuttingToolType);
	cuttingTools: CuttingTool[] = [];
	editEnabled = true;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerSecondaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	item: ProductionToolProgram = new ProductionToolProgram();
	itemType = "Tooling Worksheet";
	lines: ProductionToolProgramLine[] = [];
	machineCentreButtons: any[] = [
		{ location: "after", name: "bestMachine", options: { hint: "Pick the smallest machine the billet fits", icon: "fal fa-magic", onClick: () => this.selectBestMachine(), stylingMode: "text" } },
		"dropDown",
	];
	machines: Machine[] = [];
	// Header card values: the program's part and material are fixed by the tool and billet, so they're shown, not edited.
	get materialName(): string {
		return this.materials.find((m: any) => m.id === this.item.materialType)?.name || "";
	}

	materials = GetPropertiesFromEnum(MaterialType).filter((m: any) => m.id !== MaterialType.NotApplicable);
	// "OP10 FULL PROGRAM" - shown after the sheet title.
	operationLabel = "";
	parts: any[] = [];

	// Program ids are shown to four digits: 3 shows as 0003.
	get programNumber(): string {
		return this.item.id ? String(this.item.id).padStart(4, "0") : "";
	}

	get programPart(): any {
		return this.parts.find((p: any) => p.id === this.item.partId);
	}

	productionToolId = 0;
	productionToolImages: SupportingDocumentFileLineData[] = [];

	// The tool's first image, shown in the header opposite the part's.
	get productionToolImageUrl(): string {
		return this.productionToolImages.find((i: any) => i?.fileUrl)?.fileUrl || "";
	}

	productionToolName = "";
	// There's no operator on the program itself - it's per cutting tool line - so this is the one they all share.
	programOperatorId: number | null = null;
	programmers: UserProfile[] = [];
	readOnly = true;
	returnUrl = "admin/productionTools";
	roughFinishes: any;
	// How the job is held: a vice, or clamped straight to the bed. Stored as ViceSetUp.
	setUpModes = [{ id: true, name: "Vice" }, { id: false, name: "Clamp to Bed" }];
	showMachineCoords = false;
	showSetterInstructions = false;
	title: string = "View " + this.itemType;
	toolHolderMachines: ToolHolderMachine[] = [];
	toolHolders: ToolHolder[] = [];
	toolOrderId = 0;
	toolroomOperators: UserProfile[] = [];
	constructor(
		private router: Router,
		private toastService: ToastService,
		private route: ActivatedRoute,
		private itemService: ProductionToolProgramService,
		private lineService: ProductionToolProgramLineService,
		private cuttingToolService: CuttingToolService,
		private toolHolderService: ToolHolderService,
		private toolHolderMachineService: ToolHolderMachineService,
		private machineService: MachineService,
		private productionToolService: ProductionToolService,
		private productionToolPartService: ProductionToolPartService,
		private userProfileService: UserProfileService,
		private operationService: OperationService,
		private systemParameterService: SystemParameterService,
		private toolOrderService: ToolOrderService,
		private fileService: FileService
	) {
		this.buildButtons("view");
		this.roughFinishes = GetPropertiesFromEnum(RoughFinish, true);
		this.moveLineUp = this.moveLineUp.bind(this);
		this.moveLineDown = this.moveLineDown.bind(this);
		this.operatorSignFromGrid = this.operatorSignFromGrid.bind(this);
		this.setCuttingToolCellValue = this.setCuttingToolCellValue.bind(this);
		this.diameterForRow = this.diameterForRow.bind(this);
		this.setDiameterCellValue = this.setDiameterCellValue.bind(this);
		this.setToolTypeCellValue = this.setToolTypeCellValue.bind(this);
		this.toolTypeForRow = this.toolTypeForRow.bind(this);
		this.uploadProgramImage = this.uploadProgramImage.bind(this);
		this.loadReferenceData();
		this.getProgrammers();
		this.route.params
			.subscribe((params) => {
				if (params.id != "create") {
					if (params.id.split("_").length > 1) {
						this.getItem(params.id.split("_")[0]);
						this.edit();
					} else {
						this.getItem(params.id);
					}
				} else {
					this.create();
				}
			});
	}

	// The planner reads machine and operator from the operation, so keep it in step with the program.
	applyToOperation(programId: number) {
		if (!programId || !this.item.operationId) {
			return;
		}
		this.itemService.applyToOperation(programId)
			.subscribe(
				() => console.log("Operation updated from program"),
				(err) => { console.log(err); ToastApiError(this.toastService, err, "The billet's operation could not be updated with this machine and operator"); }
			);
	}

	buildButtons(mode: string) {
		this.headerPrimaryButtons = [];
		this.headerTertiaryButtons = [];
		if (mode == "edit" && this.editEnabled) {
			this.headerPrimaryButtons = [
				{ method: "updateItem", text: "Save Changes" },
				{ method: "cancelEditing", text: "View Mode" },
				{ method: "close", text: "Close" }
			];
		} else if (mode == "create" && this.createEnabled) {
			this.headerPrimaryButtons = [
				{ method: "createItem", text: "Save" },
				{ method: "close", text: "Close" }
			];
		} else if (mode == "view") {
			this.headerPrimaryButtons = [
				{ method: "edit", text: "Edit" },
				{ method: "print", text: "Print" },
				{ method: "close", text: "Close" }
			];

			if (this.item.id > 0 && this.item.isToolComplete === false) {
				this.headerPrimaryButtons.push({ method: "completeTool", text: "Mark Tool Complete" });
			}
		}
	}

	cancelEditing() {
		if (confirm("Are you sure you wish to switch modes? Unsaved changes will be discarded.") == false) return;
		this.readOnly = true;
		this.buildButtons("view");
		this.title = "View " + this.itemType;
	}

	close() {
		if (this.toolOrderId > 0) this.router.navigate([`/admin/toolOrders/${this.toolOrderId}`]);
		else if (this.productionToolId > 0) this.router.navigate([`/admin/productionTools/${this.productionToolId}`]);
		else this.router.navigate([`/${this.returnUrl}/`]);
	}

	// The API refuses this until the tool's storage details are filled in, and returns which are missing.
	completeTool() {
		if (confirm("Mark this tool as complete?") === false) {
			return;
		}
		this.itemService.completeTool(this.item.id)
			.subscribe(
				() => {
					this.toastService.success("Tool marked complete");
					this.getItem(this.item.id);
				},
				(err: any) => {
					console.log(err);
					ToastApiError(this.toastService, err, "Could not mark the tool complete.");
				}
			);
	}

	create() {
		this.readOnly = false;
		this.buildButtons("create");
		this.title = "Create New " + this.itemType;
		(this.item as any).viceSetUp = null;
		this.userProfileService.getCurrentUserProfile()
			.subscribe(
				(res: any) => {
					if (!this.item.programmerUserProfileId && res.response?.id) {
						this.setProgrammer(res.response.id);
					}
				},
				(err) => console.log(err)
			);
		// Pull productionToolId from query params and pre-fill
		this.route.queryParams.subscribe((qp) => {
			if (qp.operationId) {
				this.item.operationId = +qp.operationId;
				this.openExistingProgramForOperation(+qp.operationId);
				this.loadOperationLabel(+qp.operationId);
				this.fillOperatorFromOperation(+qp.operationId);
			}
			if (qp.toolOrderId) {
				this.toolOrderId = +qp.toolOrderId;
				// Added from a billet, so start the program linked to it - saving persists the link.
				this.item.blockToolOrderIds = [this.toolOrderId];
				this.fillFromBillet(this.toolOrderId);
			}
			if (qp.productionToolId) {
				this.productionToolId = +qp.productionToolId;
				this.item.productionToolId = this.productionToolId;
				this.loadProductionToolContext();
			}
		});
	}

	createItem() {
		if (!this.isValid()) {
			return;
		}
		// Programs are known by their id, and the API renames a new program to its id once it's saved. An API
		// from before that change still insists on a name that's unique for the tool, so send a stand-in.
		if (!this.item.programName) {
			this.item.programName = `NEW-${Date.now()}`;
		}
		this.itemService.createSingle(this.item)
			.subscribe(
				(res: any) => {
					const created: ProductionToolProgram = res.response;
					this.toastService.success("Successfully Created " + this.itemType);
					// Persist any lines added during creation against the new program id. Once they're all in, the
					// operator they share can be copied to the operation along with the machine.
					const linesToCreate = this.lines.filter((l) => !l.id);
					const lineRequests = linesToCreate.map((line, idx) => {
						line.productionToolProgramId = created.id;
						line.sequenceOrder = idx;
						return this.lineService.createSingle(line);
					});
					const linesSaved$ = lineRequests.length > 0 ? forkJoin(lineRequests) : of([]);

					linesSaved$.subscribe(
						() => this.applyToOperation(created.id),
						(err) => { console.log(err); ToastApiError(this.toastService, err, "Some cutting tools could not be saved"); }
					);
					this.saveBlocks(created.id);
					this.router.navigate([`/admin/productionToolPrograms/${created.id}_edit`]);
				},
				(err) => { console.log(err); ToastApiError(this.toastService, err); }
			);
	}

	diameterForRow(rowData: any) {
		// The line's own diameter wins; otherwise it comes from the chosen cutting tool. Resolved from the tool
		// list so it shows on new rows too, before the joined cuttingTool object has been loaded back.
		if (rowData.diameter != null && rowData.diameter !== "") { return rowData.diameter; }
		const tool = this.cuttingTools.find((t) => t.id === rowData.cuttingToolId);
		return tool ? tool.diameter : (rowData.cuttingTool ? rowData.cuttingTool.diameter : "");
	}

	edit() {
		this.readOnly = false;
		this.buildButtons("edit");
		this.title = "Edit " + this.itemType;
		this.toastService.warn("You are now editing...");
	}

	// Run time is stored as total minutes; these split/join it for the hours + minutes line editor.
	// Billet size, material and image come from the billet the program is for. Each only fills a blank field,
	// so anything typed over or uploaded stays.
	fillFromBillet(toolOrderId: number) {
		this.toolOrderService.getSingleById(toolOrderId)
			.subscribe(
				(res: any) => {
					const billet = res.response;

					if (!billet) {
						return;
					}

					const size = [billet.billetLength, billet.billetWidth, billet.billetHeight];

					if (!this.item.blockSize && size.every((n) => n)) {
						this.item.blockSize = size.map((n) => Number(n)).join(" x ") + " mm";
					}
					if (this.item.materialType == null && billet.material != null) {
						this.item.materialType = billet.material;
					}
					if (!this.item.imageUrl && billet.imageUrl) {
						this.item.imageUrl = billet.imageUrl;
					}
				},
				(err) => console.log(err)
			);
	}

	// The operator planned for the operation, from the ops planner, is where a new program starts.
	fillOperatorFromOperation(operationId: number) {
		this.operationService.getSingleById(operationId)
			.subscribe(
				(res: any) => {
					const operatorId = res.response?.assignedUserProfileId;

					if (operatorId && !this.programOperatorId) {
						this.setProgramOperator(operatorId);
					}
				},
				(err) => console.log(err)
			);
	}

	formatRunTime(value: any): string {
		const total = value || 0;
		if (!total) { return "-"; }
		const hours = Math.floor(total / 60);
		const minutes = total % 60;
		return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
	}

	getItem(itemId: number) {
		this.itemService.getWithRelations(itemId)
			.subscribe(
				(res: any) => {
					this.item = res.response;
					this.productionToolId = this.item.productionToolId;
					this.productionToolName = (this.item as any).productionTool?.name ?? "";
					// This endpoint returns the program itself, whose billet links are ProgramBlocks rather than the
					// DTO's BlockToolOrderIds. Without this the billets box loaded empty, and saving then cleared the links.
					this.item.blockToolOrderIds = ((res.response as any).programBlocks || []).map((b: any) => b.toolOrderId);
					this.loadProductionToolContext();
					this.getLines();
					this.restoreBilletFromOperation();
					this.loadOperationLabel(this.item.operationId);
				},
				(err) => { console.log(err); ToastApiError(this.toastService, err); }
			);
	}

	getLines() {
		this.lineService.forProgram(this.item.id)
			.subscribe(
				(res: any) => {
					this.lines = res.response;
					const operators = Array.from(new Set(this.lines.map((l) => l.operatorUserProfileId)));
					// Blank when the lines have different operators, or none, so nothing is implied.
					this.programOperatorId = operators.length === 1 && operators[0] ? operators[0] : null;
				},
				(err) => console.log(err)
			);
	}

	getProgrammers() {
		this.userProfileService.getActiveUsers()
			.subscribe(
				(res: any) => this.programmers = ((res.response || []) as UserProfile[]).sort((a, b) => (a.fullName || "").localeCompare(b.fullName || "")),
				(err) => console.log(err)
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	// The sheet's required fields, checked before saving so the user is told what's missing rather than
	// getting the API's validation error. A vice stickout only means something on a vice set up.
	isValid(): boolean {
		const missing: string[] = [];

		if (!this.item.machineId) missing.push("Machine Centre");
		if (!this.item.partId) missing.push("Part (link one to the tool)");
		if (!this.item.programmerUserProfileId) missing.push("Programmed By");
		if (!(this.item.datum || "").trim()) missing.push("Datum");
		if ((this.item as any).viceSetUp == null) missing.push("Set Up (Vice or Clamp to Bed)");

		if (missing.length > 0) {
			this.toastService.error("Still needed: " + missing.join(", "));
			return false;
		}

		if (!this.item.viceSetUp) {
			this.item.viceStickout = "";
		}
		return true;
	}

	// Sequence is stored from 0; people count lines from 1.
	lineNumber(cellInfo: any) {
		return cellInfo.value != null ? String(cellInfo.value + 1) : "";
	}

	loadOperationLabel(operationId?: number) {
		if (!operationId) {
			this.operationLabel = "";
			return;
		}
		this.operationService.getSingleById(operationId)
			.subscribe(
				(res: any) => {
					const operation = res.response;
					this.operationLabel = operation ? `OP${(operation.stageNumber || 0) * 10}${operation.processDescription ? " " + operation.processDescription : ""}` : "";
				},
				(err) => console.log(err)
			);
	}

	loadProductionToolContext() {
		if (this.productionToolId == 0) return;
		this.productionToolService.getSingleById(this.productionToolId).subscribe(
			(res: any) => {
				this.productionToolName = res.response.name;
				// Parse the parent tool's image list so the program's image picker can choose from it.
				try {
					const parsed = res.response.imageURLs ? JSON.parse(res.response.imageURLs) : [];
					this.productionToolImages = Array.isArray(parsed) ? parsed : [];
				} catch (_) {
					this.productionToolImages = [];
				}
			},
			(err) => console.log(err)
		);
		this.productionToolPartService.forProductionToolId(this.productionToolId).subscribe(
			(res: any) => {
				const links: any[] = res.response;
				// Distinct list of parts for this production tool — used as a picker for PartId
				const seen = new Set<number>();
				this.parts = links
					.filter((l) => l.part && !seen.has(l.partId) && (seen.add(l.partId), true))
					.map((l) => l.part);
				// Auto-select the only part if there's exactly one
				// Part is read only on the sheet, so a new program takes the tool's part - the first, if the tool has several.
				if (this.parts.length > 0 && !this.item.partId) this.item.partId = this.parts[0].id;
			},
			(err) => console.log(err)
		);
		// The tool's blocks (tool orders) — offered as a multi-select so a program can be grouped onto several
		// blocks that are machined together.
		this.toolOrderService.forProductionTool(this.productionToolId).subscribe(
			(res: any) => {
				const orders: any[] = res.response || [];
				this.blocks = orders.map((o) => ({ id: o.id, label: o.name || ("Billet " + o.id) }));
			},
			(err) => console.log(err)
		);
	}

	loadReferenceData() {
		this.cuttingToolService.getAll().subscribe(
			(res: any) => this.cuttingTools = (res.response.items as CuttingTool[]).filter((t) => t.isActive),
			(err) => console.log(err)
		);
		this.toolHolderService.getAll().subscribe(
			(res: any) => this.toolHolders = (res.response.items as ToolHolder[]).filter((t) => t.isActive),
			(err) => console.log(err)
		);
		// Machines and operators are both filtered by the configured toolroom site: a program is
		// written for a toolroom machine, and the full list is every machine in the business. If the
		// SystemParam hasn't been seeded (fresh database), leave the dropdowns empty rather than
		// blocking the page with an error toast.
		this.systemParameterService.getValueByName("ToolroomSettings").subscribe(
			(value: any) => {
				const siteId = value?.ToolroomSiteId;

				if (!siteId) {
					return;
				}
				this.userProfileService.bySite(siteId).subscribe(
					(res: any) => this.toolroomOperators = res.response,
					(err) => console.log(err)
				);
				this.machineService.getAll().subscribe(
					(res: any) => {
						// Back office kit at the toolroom site (fork trucks, skips, slings) isn't somewhere a program runs.
						this.machines = (res.response.items as Machine[])
							.filter((m) => m.siteId === siteId && m.department !== Department.BackOffice)
							.sort((a, b) => (a.cellNumber || "").localeCompare(b.cellNumber || ""));
					},
					(err) => console.log(err)
				);
			},
			(err) => console.log("ToolroomSettings SystemParam not configured:", err)
		);
	}

	machineToolHolders(): ToolHolder[] {
		// Filter holders by the holders linked to the selected machine
		if (!this.item.machineId) return this.toolHolders;
		// We don't yet have the holder<->machine map for the chosen machine, so as a pragmatic v1 we
		// return all active holders. A future iteration can call toolHolderMachineService.forMachine
		// and intersect.
		return this.toolHolders;
	}

	moveLine(line: ProductionToolProgramLine, delta: number) {
		const idx = this.lines.indexOf(line);
		const target = idx + delta;
		if (target < 0 || target >= this.lines.length) return;
		const tmp = this.lines[target];
		this.lines[target] = line;
		this.lines[idx] = tmp;
		this.lines = [...this.lines];
		this.lines.forEach((l, i) => l.sequenceOrder = i);
		this.persistOrder();
	}

	moveLineDown(e: any) { this.moveLine(e.row.data, 1); }

	moveLineUp(e: any) { this.moveLine(e.row.data, -1); }

	onInitNewRow(e: any) {
		e.data.id = 0;
		e.data.productionToolProgramId = this.item.id;
		// New lines go on the end: after line 8 comes line 9.
		e.data.sequenceOrder = this.lines.length;
		e.data.operatorUserProfileId = this.programOperatorId;
	}

	onRowInserted(e: any) {
		const line: ProductionToolProgramLine = e.data;
		line.sequenceOrder = this.lines.indexOf(line);

		// Only persist immediately when the program is saved; otherwise createItem persists on save.
		if (this.item.id > 0) {
			line.productionToolProgramId = this.item.id;
			this.lineService.createSingle(line)
				.subscribe(
					(res: any) => { line.id = res.response.id; this.toastService.success("Line added"); },
					(err) => { console.log(err); ToastApiError(this.toastService, err); }
				);
		}
	}

	onRowRemoved(e: any) {
		const line: ProductionToolProgramLine = e.data;
		if (line.id > 0) {
			this.lineService.deleteSingleById(line.id)
				.subscribe(
					() => this.toastService.success("Line removed"),
					(err) => { console.log(err); ToastApiError(this.toastService, err); }
				);
		}
		this.lines.forEach((l, i) => l.sequenceOrder = i);
		this.persistOrder();
	}

	onRowUpdated(e: any) {
		const line: ProductionToolProgramLine = e.data;
		if (line.id > 0) {
			this.lineService.updateSingleById(line.id, line)
				.subscribe(
					() => this.toastService.success("Line saved"),
					(err) => { console.log(err); ToastApiError(this.toastService, err); }
				);
		}
	}

	onToolbarPreparing(e: any) {
		// Drop the grid's built-in add button — we add rows from the explicit "Add Row" button.
		const items = e.toolbarOptions.items || [];
		const idx = items.findIndex((i: any) => i.name === "addRowButton");
		if (idx >= 0) items.splice(idx, 1);
	}

	// An operation has one program. If it already has one, open that rather than starting a second.
	openExistingProgramForOperation(operationId: number) {
		this.itemService.forOperation(operationId)
			.subscribe(
				(res: any) => {
					const existing = (res.response || [])[0];

					if (existing) {
						this.router.navigate([`/admin/productionToolPrograms/${existing.id}_edit`], { replaceUrl: true });
					}
				},
				(err) => console.log(err)
			);
	}

	operatorSignFromGrid(e: any) { this.signOffOperator(e.row.data); }

	persistOrder() {
		const persisted = this.lines.filter((l) => l.id > 0);
		if (persisted.length === 0) return;
		this.lineService.reorder(this.item.id, persisted.map((l) => l.id))
			.subscribe(
				() => { /* no-op */ },
				(err) => { console.log(err); ToastApiError(this.toastService, err, "Reorder failed"); }
			);
	}

	print() {
		window.open(`/admin/productionToolPrograms/${this.item.id}/print`, "_blank");
	}

	// A program belongs to its operation's billet. If it has no billet links - including programs whose links
	// were cleared by the old load bug - link that billet back, then fill anything blank from it.
	restoreBilletFromOperation() {
		const fillBlanks = (billetId: number) => {
			if (!this.item.blockSize || this.item.materialType == null || !this.item.imageUrl) {
				this.fillFromBillet(billetId);
			}
		};

		if ((this.item.blockToolOrderIds || []).length > 0) {
			fillBlanks(this.item.blockToolOrderIds![0]);
			return;
		}

		if (!this.item.operationId) {
			return;
		}

		this.operationService.getSingleById(this.item.operationId)
			.subscribe(
				(res: any) => {
					const billetId = res.response?.toolOrderId;

					if (billetId) {
						this.item.blockToolOrderIds = [billetId];
						fillBlanks(billetId);
					}
				},
				(err) => console.log(err)
			);
	}

	runTimeHours(value: any): number { return Math.floor((value || 0) / 60); }

	runTimeMinutes(value: any): number { return (value || 0) % 60; }

	// Persist the grouped blocks (tool orders) this program is machined on.
	saveBlocks(programId: number) {
		this.itemService.setBlocks(programId, this.item.blockToolOrderIds || [])
			.subscribe(
				() => { /* no-op */ },
				(err) => { console.log(err); ToastApiError(this.toastService, err, "Could not save grouped billets."); }
			);
	}

	// The smallest toolroom machine whose working area takes the billet. A billet can sit on any face, so the
	// billet's sides and the machine's axes are each sorted largest first and compared pair by pair - for two
	// boxes that is exactly "fits in some orientation". Smallest means least working volume. The size is read
	// from Billet Size, which is filled from the billet, so a size typed over it is what gets used.
	selectBestMachine() {
		if (this.readOnly) {
			this.toastService.warn("Switch to edit mode to change the machine");
			return;
		}

		const sizes = (this.item.blockSize || "").match(/\d+(\.\d+)?/g);

		if (!sizes || sizes.length < 3) {
			this.toastService.error("Billet Size needs a length, width and height - e.g. 500 x 300 x 200 mm");
			return;
		}

		const largestFirst = (values: number[]) => [...values].sort((a, b) => b - a);
		const billet = largestFirst(sizes.slice(0, 3).map(Number));
		const measured = this.machines.filter((m) => m.workingAreaX && m.workingAreaY && m.workingAreaZ);

		if (measured.length === 0) {
			this.toastService.error("No toolroom machine has a working area set - add X, Y and Z on each machine");
			return;
		}

		const fits = measured
			.map((machine) => {
				const axes = largestFirst([Number(machine.workingAreaX), Number(machine.workingAreaY), Number(machine.workingAreaZ)]);
				return { axes, machine, volume: axes[0] * axes[1] * axes[2] };
			})
			.filter((c) => c.axes.every((axis, i) => axis >= billet[i]))
			.sort((a, b) => a.volume - b.volume);

		const unmeasured = this.machines.length - measured.length;
		const skipped = unmeasured > 0 ? ` ${unmeasured} machine${unmeasured === 1 ? "" : "s"} without a working area weren't checked.` : "";

		if (fits.length === 0) {
			this.toastService.error(`No toolroom machine's working area takes a ${billet.join(" x ")} mm billet.${skipped}`);
			return;
		}

		this.item.machineId = fits[0].machine.id;
		this.toastService.success(`${fits[0].machine.cellNumber} is the smallest machine the billet fits.${skipped}`);
	}

	setCuttingToolCellValue(newData: any, value: any, currentRowData: any) {
		newData.cuttingToolId = value;
		const tool = this.cuttingTools.find((t) => t.id === value);
		if (!tool) { return; }
		// Roughing vs Finishing decides which of the cutter's material speeds/feeds to use.
		const roughFinish = currentRowData.roughFinish != null ? currentRowData.roughFinish : tool.defaultRoughFinish;
		// Prefer the cutter's speed/feed for the worksheet's material; fall back to the flat default.
		const setting = this.settingForTool(tool, this.item.materialType);
		let speed = tool.defaultSpeed;
		let feed = tool.defaultFeed;
		if (setting) {
			if (roughFinish === RoughFinish.Finishing) {
				speed = setting.finishingSpeed != null ? setting.finishingSpeed : speed;
				feed = setting.finishingFeed != null ? setting.finishingFeed : feed;
			} else {
				speed = setting.roughingSpeed != null ? setting.roughingSpeed : speed;
				feed = setting.roughingFeed != null ? setting.roughingFeed : feed;
			}
		}
		// Fill blanks only — never overwrite an existing per-line value.
		if (currentRowData.stickOut == null) { newData.stickOut = tool.defaultStickOut; }
		if (currentRowData.speed == null) { newData.speed = speed; }
		if (currentRowData.feed == null) { newData.feed = feed; }
		if (currentRowData.roughFinish == null) { newData.roughFinish = tool.defaultRoughFinish; }
	}

	// Sets the operator on every cutting tool line. Saved lines are updated straight away; lines not yet saved
	// carry it when the program is created.
	// Typing over the diameter or type stores an override on the line. Clearing it hands the field back to the
	// cutting tool, so a line only carries a value where it genuinely differs from the library.
	setDiameterCellValue(newData: any, value: any) {
		newData.diameter = value === "" ? null : value;
	}

	setProgramOperator(operatorId: number | null) {
		this.programOperatorId = operatorId;
		this.lines.forEach((line) => line.operatorUserProfileId = operatorId as any);
		this.lines = [...this.lines];

		const saved = this.lines.filter((line) => line.id > 0);

		if (saved.length === 0) {
			return;
		}

		forkJoin(saved.map((line) => this.lineService.updateSingleById(line.id, { operatorUserProfileId: operatorId } as any)))
			.subscribe(
				() => {
					this.toastService.success(`Operator set on ${saved.length} cutting tool${saved.length === 1 ? "" : "s"}`);
					// Only once every line has saved does the program have one operator to give its operation.
					this.applyToOperation(this.item.id);
				},
				(err) => { console.log(err); ToastApiError(this.toastService, err, "The operator could not be set on every cutting tool"); }
			);
	}

	// Who programmed it, and when they were recorded as having done so.
	setProgrammer(userProfileId: number | null) {
		if (this.item.programmerUserProfileId !== userProfileId) {
			this.item.programmerSignedAt = userProfileId ? new Date() : undefined;
		}
		this.item.programmerUserProfileId = userProfileId as any;
	}

	setRunTimeHours(cell: any, hours: any) {
		cell.setValue((hours || 0) * 60 + ((cell.value || 0) % 60));
	}

	setRunTimeMinutes(cell: any, minutes: any) {
		cell.setValue(Math.floor((cell.value || 0) / 60) * 60 + (minutes || 0));
	}

	setToolTypeCellValue(newData: any, value: any) {
		newData.toolType = value;
	}

	settingForTool(tool: any, materialType: any): CuttingToolSpeedFeedSetting | undefined {
		if (!tool || !tool.speedFeedSettings || materialType == null) { return undefined; }
		try {
			const settings: CuttingToolSpeedFeedSetting[] = JSON.parse(tool.speedFeedSettings);
			return settings.find((s) => s.materialType === materialType);
		} catch (e) {
			return undefined;
		}
	}

	signOffOperator(line: ProductionToolProgramLine) {
		if (!line.operatorUserProfileId) { this.toastService.warn("Pick an operator first."); return; }
		this.lineService.operatorSign(line.id, line.operatorUserProfileId)
			.subscribe(
				(res: any) => {
					line.signedAt = res.response.signedAt;
					this.toastService.success("Operator signed");
				},
				(err) => { console.log(err); ToastApiError(this.toastService, err); }
			);
	}

	// The cutter type belongs to the chosen cutting tool, like its diameter.
	toolTypeForRow(rowData: any) {
		// The line's own type wins; otherwise it comes from the chosen cutting tool.
		if (rowData.toolType != null) { return rowData.toolType; }
		const tool = this.cuttingTools.find((t) => t.id === rowData.cuttingToolId) || rowData.cuttingTool;
		return tool ? tool.toolType : null;
	}

	updateItem() {
		if (!this.isValid()) {
			return;
		}
		this.itemService.updateSingleById(this.item.id, this.item)
			.subscribe(
				() => { this.toastService.success("Successfully Updated"); this.saveBlocks(this.item.id); },
				(err) => { console.log(err); ToastApiError(this.toastService, err); }
			);
	}

	// A picture for this program, replacing the one taken from the billet.
	uploadProgramImage(file: File) {
		const containerName = "images";
		const uploadData = new FormData();
		uploadData.append("containerName", containerName);
		uploadData.append("files", file);
		uploadData.append("mimeTypeKey", "ImageMimeTypes");
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					this.item.imageUrl = `${environment.server.storageUrl}/${containerName}/${res.response[0].location}`;
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err, "Image upload failed - please upload a valid image file.");
				}
			);
	}

}
